/**
 * Pharmacy management utilities for AI Prescription
 * Pharmacy data comes from WSS only (no Redux).
 */

import { Pharmacy } from './types';

/**
 * Updates pharmacy information from WebSocket data
 * Only updates if new data is provided, never clears existing data
 */
export const updatePharmacyFromWebSocket = (
  data: any,
  setPreferredPharmacy: (value: string) => void,
  setPharmacyIdFromAPI: (value: string | null) => void
) => {
  if (!data) {
    return; // Don't update if invalid, keep existing data
  }

  // If status_code is provided, it must be 200. Otherwise, assume it's valid data.
  if (data.status_code !== undefined && data.status_code !== 200) {
    return; // Don't update if invalid status, keep existing data
  }

  // Set pharmacyId if available
  if (data.pharmacyId) {
    setPharmacyIdFromAPI(data.pharmacyId);
    // Persist to localStorage
    localStorage.setItem("pharmacyIdFromAPI", data.pharmacyId);
  }

  // Try to get pharmacy name from different possible fields
  let pharmacyName = '';

  // First, try 'detail' field (most common)
  if (data.detail) {
    pharmacyName = typeof data.detail === 'string' ? data.detail.trim() : '';
  }
  // Try 'pharmacyName' field
  else if (data.pharmacyName) {
    pharmacyName = typeof data.pharmacyName === 'string' ? data.pharmacyName.trim() : '';
  }
  // Try 'name' field
  else if (data.name) {
    pharmacyName = typeof data.name === 'string' ? data.name.trim() : '';
  }

  // Only update preferredPharmacy if we found a new name (don't clear if no name found)
  if (pharmacyName) {
    setPreferredPharmacy(pharmacyName);
    // Persist to localStorage
    localStorage.setItem("preferredPharmacy", pharmacyName);
  }
  // Don't clear existing data if no new name found
};

/**
 * Converts WSS pharmacyLookup to Pharmacy[] format for PharmacyList.
 * Uses preferredPharmacy as fallback when pharmacyLookup is empty.
 */
export const getPharmaciesFromPharmacyLookup = (
  pharmacyLookup: (string | any)[] | undefined,
  preferredPharmacy: string
): Pharmacy[] => {
  if (pharmacyLookup && Array.isArray(pharmacyLookup) && pharmacyLookup.length > 0) {
    const result: Pharmacy[] = [];
    pharmacyLookup.forEach((item: string | any, index: number) => {
      const name =
        typeof item === "string"
          ? item
          : item?.nameConcatenatedWithAddress ||
            item?.pharmacyName ||
            item?.name ||
            item?.pharmacy_name ||
            "";
      if (name && !(typeof name === "string" && name.toLowerCase().includes("select"))) {
        result.push({
          id: index,
          name: typeof name === "string" ? name : String(name),
          address: item?.address || "",
          city: item?.city || "",
          state: item?.state || "",
          distance: item?.distance || "",
        });
      }
    });
    return result;
  }

  if (preferredPharmacy) {
    return [{
      id: 0,
      name: preferredPharmacy,
      address: "Your selected pharmacy",
      distance: "Preferred",
    }];
  }

  return [];
};

